import { useState, useMemo, useRef, useEffect, useCallback, memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, Package, Wrench, CircleDot,
  Waves, Move, GripVertical, GripHorizontal,
  ChevronUp, ChevronDown, Minus, Plus
} from "lucide-react";

/* ═══════════════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════════════ */
const PRODUCTS = [
  { id: "miel", name: "Tropic Miel", w: 0.14, l: 2.5, t: 0.02, color: "#C4993B" },
  { id: "teck", name: "Tropic Teck", w: 0.14, l: 2.5, t: 0.02, color: "#8B6914" },
  { id: "intense", name: "Tropic Intense", w: 0.14, l: 2.0, t: 0.02, color: "#4A3728" },
];
const PLANK_W = 0.14;
const CLIPS_M2 = 20;
const DCLIPS_M2 = 2;
const LAMB_SPACE = 0.5;
const LAMB_LEN = 2.5;
const WASTE = 1.1;
const DIR = { H: "h", V: "v" };

/* ═══════════════════════════════════════════════════════
   SHAPE SVG ICONS
   ═══════════════════════════════════════════════════════ */
const ShapeIcon = ({ type, active }) => {
  const s = active ? "#8BC34A" : "#999";
  const f = active ? "rgba(139,195,74,0.15)" : "none";
  if (type === "rect") return <svg viewBox="0 0 36 28" className="w-9 h-7"><rect x="3" y="4" width="30" height="20" rx="2" fill={f} stroke={s} strokeWidth="2"/></svg>;
  if (type === "lshape") return <svg viewBox="0 0 36 28" className="w-9 h-7"><path d="M3 4h22v10h8v10H3z" fill={f} stroke={s} strokeWidth="2" strokeLinejoin="round"/></svg>;
  return null;
};

/* ═══════════════════════════════════════════════════════
   PLANKS RENDERER — Individual plank tiles, never stretched
   ═══════════════════════════════════════════════════════ */
const PlanksGrid = memo(({ direction, product, numPlanks, clipPath, isDesktop }) => {
  const isH = direction === DIR.H;
  const planks = isDesktop ? 20 : Math.min(30, numPlanks);

  return (
    <div
      className={`absolute inset-0 flex ${isH ? "flex-col" : "flex-row"}`}
      style={{ gap: "2px", clipPath }}
    >
      {Array.from({ length: planks }).map((_, i) => (
        <div
          key={`${product.id}-${direction}-${i}`}
          className="flex-1"
          style={{
            backgroundColor: product.color,
            borderRadius: "1px",
            minWidth: isH ? "auto" : "4px",
            minHeight: isH ? "4px" : "auto",
          }}
        />
      ))}
    </div>
  );
});

/* ═══════════════════════════════════════════════════════
   POOL — Realistic water + stone brocal
   ═══════════════════════════════════════════════════════ */
const PoolElement = ({ poolShape, poolPos, pctW, pctH, isDragging, onStart }) => (
  <div
    className="absolute z-10 select-none"
    style={{
      width: `${pctW}%`, height: `${pctH}%`,
      left: `${poolPos.x - pctW / 2}%`,
      top: `${poolPos.y - pctH / 2}%`,
      borderRadius: poolShape === "oval" ? "50%" : "8px",
      border: "5px solid #c8b89a",
      background: "linear-gradient(180deg, #4aa3d2 0%, #2980b9 35%, #1e6fa0 70%, #1a5d88 100%)",
      boxShadow: `
        inset 0 4px 20px rgba(0,40,80,0.5),
        inset 0 -3px 10px rgba(0,30,60,0.3),
        0 0 0 2px #a08860,
        0 4px 16px rgba(0,0,0,0.35)
      `,
      cursor: isDragging ? "grabbing" : "grab",
      touchAction: "none",
      transition: isDragging ? "none" : "box-shadow 0.2s",
    }}
    onMouseDown={(e) => { e.preventDefault(); onStart(); }}
    onTouchStart={onStart}
    data-testid="mobile-pool-preview"
  >
    {/* Water caustics */}
    <div
      className="absolute inset-0 overflow-hidden"
      style={{ borderRadius: poolShape === "oval" ? "50%" : "4px" }}
    >
      <div
        className="absolute inset-0"
        style={{
          background: "radial-gradient(ellipse at 30% 20%, rgba(255,255,255,0.3) 0%, transparent 55%)",
          animation: "water-caustics 4s ease-in-out infinite",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 8px, rgba(255,255,255,0.04) 8px, rgba(255,255,255,0.04) 10px)",
          animation: "water-lines 3s linear infinite",
        }}
      />
    </div>
    <div className="absolute inset-0 flex items-center justify-center">
      <Move className={`w-4 h-4 ${isDragging ? "text-white/70" : "text-white/20"}`} />
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════
   ESTIMATION CARD — Floating, glass-morphism, animated
   ═══════════════════════════════════════════════════════ */
const EstCard = ({ materials, product }) => {
  if (!materials) return null;
  const items = [
    { l: "Surface", v: materials.area, u: "m²", c: "#8BC34A" },
    { l: "Lames", v: materials.lames, u: "u.", c: "#C4993B" },
    { l: "Lamb.", v: materials.lambourdes, u: "u.", c: "#9E9E9E" },
    { l: "Clips", v: materials.clips, u: "u.", c: "#B0BEC5" },
    { l: "½ clips", v: materials.demiClips, u: "u.", c: "#FF8A65" },
  ];
  return (
    <motion.div
      className="rounded-2xl p-3.5 backdrop-blur-xl"
      style={{
        background: "rgba(10,10,10,0.82)",
        boxShadow: "0 8px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06)",
      }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="mobile-results"
    >
      <div className="flex items-center gap-1.5 mb-2.5">
        <span className="w-1.5 h-1.5 rounded-full bg-[#8BC34A] animate-pulse" />
        <span className="text-[10px] font-semibold text-[#8BC34A] tracking-widest uppercase">Estimation</span>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {items.map((r) => (
          <div key={r.l} className="text-center">
            <AnimatePresence mode="wait">
              <motion.span
                key={r.v}
                className="block text-base font-bold text-white tabular-nums"
                initial={{ y: -6, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 6, opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {r.v}
              </motion.span>
            </AnimatePresence>
            <span className="text-[8px] text-[#777]">{r.l}</span>
          </div>
        ))}
      </div>
      <p className="text-[7px] text-[#444] mt-2 leading-relaxed text-center">
        +10% marge · Lames {product.name} {product.t*1000}×{product.w*1000}×{product.l*1000}mm · Clips 20/m² · ½ clips 2/m²
      </p>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════ */
export function MobileCalculateur() {
  const [terrL, setTerrL] = useState(5.0);
  const [terrW, setTerrW] = useState(4.0);
  const [shape, setShape] = useState("rect");
  const [cutL, setCutL] = useState(2.0);
  const [cutW, setCutW] = useState(2.0);
  const [hasPool, setHasPool] = useState(false);
  const [poolShape, setPoolShape] = useState("rectangle");
  const [poolL, setPoolL] = useState(3.0);
  const [poolW, setPoolW] = useState(2.0);
  const [poolPos, setPoolPos] = useState({ x: 55, y: 50 });
  const [product, setProduct] = useState(PRODUCTS[0]);
  const [dir, setDir] = useState(DIR.H);
  const [isDragPool, setIsDragPool] = useState(false);
  const [dragEdge, setDragEdge] = useState(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const terrRef = useRef(null);

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* ─── Computed ────────────────────────────────── */
  const computePoolArea = () => {
    if (!hasPool) return 0;
    if (poolShape === "oval") return Math.PI * (poolL / 2) * (poolW / 2);
    return poolL * poolW;
  };
  const cutA = shape === "lshape" ? cutL * cutW : 0;
  const terrA = terrL * terrW - cutA;
  const poolA = computePoolArea();
  const netA = Math.max(terrA - poolA, 0);
  const numPlanks = Math.round((dir === DIR.H ? terrW : terrL) / PLANK_W);

  const materials = useMemo(() => {
    if (netA <= 0) return null;
    const lames = Math.ceil((netA / (product.w * product.l)) * WASTE);
    const [par, perp] = dir === DIR.H ? [terrL, terrW] : [terrW, terrL];
    const lines = Math.floor(par / LAMB_SPACE) + 1;
    const lamb = Math.max(Math.ceil((lines * perp / LAMB_LEN) * WASTE), 2);
    return { area: netA.toFixed(1), lames, lambourdes: lamb, clips: Math.ceil(netA * CLIPS_M2), demiClips: Math.ceil(netA * DCLIPS_M2), product: product.name };
  }, [netA, product, dir, terrL, terrW]);

  /* ─── Constraints ─────────────────────────────── */
  useEffect(() => { if (cutL > terrL - 0.5) setCutL(+(Math.max(0.5, terrL - 0.5)).toFixed(1)); }, [terrL, cutL]);
  useEffect(() => { if (cutW > terrW - 0.5) setCutW(+(Math.max(0.5, terrW - 0.5)).toFixed(1)); }, [terrW, cutW]);
  useEffect(() => { if (poolL > terrL - 0.5) setPoolL(+(Math.max(1, terrL - 0.5)).toFixed(1)); }, [terrL, poolL]);
  useEffect(() => { if (poolW > terrW - 0.5) setPoolW(+(Math.max(1, terrW - 0.5)).toFixed(1)); }, [terrW, poolW]);

  /* ─── Edge drag ───────────────────────────────── */
  const handleEdgeDrag = useCallback((edge) => (e) => {
    e.preventDefault();
    const el = terrRef.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    const gp = (ev) => ev.touches ? ev.touches[0] : ev;
    const s = gp(e);
    const sd = edge === "right" ? terrL : terrW;
    const ppm = edge === "right" ? rect.width / terrL : rect.height / terrW;
    setDragEdge(edge);
    const onM = (me) => {
      me.preventDefault();
      const c = gp(me);
      const d = edge === "right" ? c.clientX - s.clientX : c.clientY - s.clientY;
      const v = Math.max(1, Math.min(edge === "right" ? 15 : 10, Math.round((sd + d / ppm) * 2) / 2));
      edge === "right" ? setTerrL(v) : setTerrW(v);
    };
    const onE = () => { setDragEdge(null); document.removeEventListener("mousemove", onM); document.removeEventListener("mouseup", onE); document.removeEventListener("touchmove", onM); document.removeEventListener("touchend", onE); };
    document.addEventListener("mousemove", onM); document.addEventListener("mouseup", onE);
    document.addEventListener("touchmove", onM, { passive: false }); document.addEventListener("touchend", onE);
  }, [terrL, terrW]);

  /* ─── Pool drag ───────────────────────────────── */
  useEffect(() => {
    if (!isDragPool) return;
    const hm = (e) => {
      e.preventDefault();
      const el = terrRef.current; if (!el) return;
      const r = el.getBoundingClientRect();
      const p = e.touches ? e.touches[0] : e;
      let x = ((p.clientX - r.left) / r.width) * 100;
      let y = ((p.clientY - r.top) / r.height) * 100;
      const pw = (poolL / terrL) * 100, ph = (poolW / terrW) * 100;
      setPoolPos({ x: Math.max(pw / 2, Math.min(100 - pw / 2, x)), y: Math.max(ph / 2, Math.min(100 - ph / 2, y)) });
    };
    const he = () => setIsDragPool(false);
    document.addEventListener("mousemove", hm); document.addEventListener("mouseup", he);
    document.addEventListener("touchmove", hm, { passive: false }); document.addEventListener("touchend", he);
    return () => { document.removeEventListener("mousemove", hm); document.removeEventListener("mouseup", he); document.removeEventListener("touchmove", hm); document.removeEventListener("touchend", he); };
  }, [isDragPool, poolL, poolW, terrL, terrW]);

  /* ─── Geometry ────────────────────────────────── */
  const mx = Math.max(terrL, terrW, 1);
  const tW = (terrL / mx) * 82;
  const tH = (terrW / mx) * 82;
  const ppW = (poolL / terrL) * 100;
  const ppH = (poolW / terrW) * 100;
  const aspectRatio = terrL / terrW;

  const clipPath = (() => {
    if (shape === "lshape") {
      const cx = ((terrL - cutL) / terrL) * 100, cy = (cutW / terrW) * 100;
      return `polygon(0% 0%, ${cx}% 0%, ${cx}% ${cy}%, 100% ${cy}%, 100% 100%, 0% 100%)`;
    }
    return "none";
  })();

  /* ─── Compact NumInput with stepper buttons ────────────────────────── */
  const NI = ({ value, onChange, min, max, label, color = "#fff" }) => {
    const [localVal, setLocalVal] = useState(null);
    const displayVal = localVal !== null ? localVal : value.toFixed(1);
    const step = (d) => {
      const next = Math.round((value + d * 0.5) * 2) / 2;
      onChange(Math.max(min, Math.min(max, next)));
    };
    return (
      <div className="flex items-center justify-between py-1.5">
        <span className="text-sm md:text-[11px] text-[#ccc]">{label}</span>
        <div className="flex items-center gap-1">
          {/* Minus button (desktop only) */}
          <button
            onClick={() => step(-1)}
            className="hidden md:flex items-center justify-center w-7 h-7 rounded-md bg-[#1a1a1a] border border-[#333] hover:border-[#8BC34A] hover:bg-[#1f1f1f] active:bg-[#8BC34A]/20 transition-colors"
            data-testid={`${label}-minus`}
          >
            <Minus className="w-3 h-3" style={{ color }} />
          </button>
          <input
            type="number" inputMode="decimal" value={displayVal} step={0.5} min={min} max={max}
            onFocus={() => setLocalVal(value.toFixed(1))}
            onChange={(e) => setLocalVal(e.target.value)}
            onBlur={(e) => {
              const v = parseFloat(e.target.value);
              if (!isNaN(v)) onChange(Math.max(min, Math.min(max, Math.round(v * 2) / 2)));
              setLocalVal(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.target.blur(); }
            }}
            className="w-[72px] md:w-[52px] text-center text-sm md:text-xs font-semibold rounded-lg py-2.5 md:py-1.5 border border-[#333] focus:border-[#8BC34A] outline-none"
            style={{ background: "#1a1a1a", color }}
          />
          {/* Plus button (desktop only) */}
          <button
            onClick={() => step(1)}
            className="hidden md:flex items-center justify-center w-7 h-7 rounded-md bg-[#1a1a1a] border border-[#333] hover:border-[#8BC34A] hover:bg-[#1f1f1f] active:bg-[#8BC34A]/20 transition-colors"
            data-testid={`${label}-plus`}
          >
            <Plus className="w-3 h-3" style={{ color }} />
          </button>
          <span className="text-xs md:text-[9px] text-[#999]">m</span>
        </div>
      </div>
    );
  };

  return (
    <div
      className="max-w-[1400px] mx-auto"
      style={{ fontFamily: "'Inter', sans-serif" }}
      data-testid="calculator-wrapper"
    >
      <div className="flex flex-col md:flex-row md:h-[calc(100vh-120px)] md:min-h-[520px] gap-0 md:rounded-2xl overflow-visible md:overflow-hidden" style={{ border: "1px solid #1f1f1f" }}>

        {/* ═══ SIDEBAR ═══════════════════════════════ */}
        <aside
          className="w-full md:w-64 order-3 md:order-1 flex-shrink-0 overflow-y-auto"
          style={{ background: "#111" }}
          data-testid="sidebar"
        >
          <div className="p-4 space-y-4">

            {/* ── Couleur ── */}
            <section>
              <h4 className="text-xs md:text-[10px] font-semibold text-[#bbb] tracking-[0.15em] uppercase mb-3">Couleur</h4>
              <div className="flex items-center gap-3">
                {PRODUCTS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setProduct(p)}
                    className={`group relative w-11 h-11 rounded-full transition-all duration-200 ${
                      product.id === p.id ? "ring-[2.5px] ring-[#8BC34A] ring-offset-2 ring-offset-[#111] scale-110" : "ring-1 ring-[#333] hover:ring-[#555]"
                    }`}
                    data-testid={`mobile-product-${p.id}`}
                  >
                    <div className="w-full h-full rounded-full" style={{ backgroundColor: p.color }} />
                    <span className={`absolute -bottom-4 left-1/2 -translate-x-1/2 text-[9px] md:text-[8px] font-medium whitespace-nowrap ${product.id === p.id ? "text-[#8BC34A]" : "text-[#aaa]"}`}>
                      {p.name.replace("Tropic ", "")}
                    </span>
                  </button>
                ))}
              </div>
            </section>

            <div className="border-t border-[#1f1f1f]" />

            {/* ── Forme ── */}
            <section>
              <h4 className="text-xs md:text-[10px] font-semibold text-[#bbb] tracking-[0.15em] uppercase mb-3">Forme</h4>
              <div className="flex gap-2">
                {["rect", "lshape"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setShape(s)}
                    className={`flex-1 flex items-center justify-center py-3 md:py-2 rounded-xl transition-all ${
                      shape === s ? "bg-[#1a1a1a] ring-1 ring-[#8BC34A]/40" : "bg-[#0d0d0d] hover:bg-[#1a1a1a]"
                    }`}
                    data-testid={`shape-${s}`}
                  >
                    <ShapeIcon type={s} active={shape === s} />
                  </button>
                ))}
              </div>
            </section>

            {/* ── Pose ── */}
            <section>
              <h4 className="text-xs md:text-[10px] font-semibold text-[#bbb] tracking-[0.15em] uppercase mb-2">Sens de pose</h4>
              <div className="flex gap-1">
                {[{ d: DIR.H, l: "Horizontal" }, { d: DIR.V, l: "Vertical" }].map((o) => (
                  <button
                    key={o.d}
                    onClick={() => setDir(o.d)}
                    className={`flex-1 py-2 md:py-1.5 rounded-lg text-sm md:text-[11px] font-medium transition-all ${
                      dir === o.d ? "bg-[#1a1a1a] text-white ring-1 ring-[#333]" : "text-[#aaa] hover:text-white"
                    }`}
                    data-testid={`mobile-dir-${o.d}`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </section>

            <div className="border-t border-[#1f1f1f]" />

            {/* ── Dimensions ── */}
            <section>
              <h4 className="text-xs md:text-[10px] font-semibold text-[#bbb] tracking-[0.15em] uppercase mb-2">Dimensions</h4>
              <NI label="Longueur" value={terrL} onChange={setTerrL} min={1} max={15} color="#8BC34A" />
              <NI label="Largeur" value={terrW} onChange={setTerrW} min={1} max={10} color="#8BC34A" />
              <AnimatePresence>
                {shape === "lshape" && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="pt-1 mt-1 border-t border-[#1f1f1f]">
                    <span className="text-[9px] text-[#FF8A65] font-medium">Découpe L</span>
                    <NI label="Long." value={cutL} onChange={setCutL} min={0.5} max={Math.max(terrL - 1, 0.5)} color="#FF8A65" />
                    <NI label="Larg." value={cutW} onChange={setCutW} min={0.5} max={Math.max(terrW - 1, 0.5)} color="#FF8A65" />
                  </motion.div>
                )}
              </AnimatePresence>
            </section>

            <div className="border-t border-[#1f1f1f]" />

            {/* ── Piscine ── */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs md:text-[10px] font-semibold text-[#bbb] tracking-[0.15em] uppercase flex items-center gap-1.5">
                  <Waves className="w-3 h-3 text-[#29B6F6]" /> Piscine
                </h4>
                <button
                  onClick={() => setHasPool(!hasPool)}
                  className={`relative w-12 h-6 md:w-10 md:h-5 rounded-full transition-all ${hasPool ? "bg-[#29B6F6]" : "bg-[#2a2a2a]"}`}
                  data-testid="pool-toggle"
                >
                  <div className={`absolute top-0.5 w-5 h-5 md:w-4 md:h-4 rounded-full bg-white shadow transition-all ${hasPool ? "left-6 md:left-5.5" : "left-0.5"}`} />
                </button>
              </div>
              <AnimatePresence>
                {hasPool && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                    <div className="flex gap-1.5 mb-2">
                      {[{ id: "rectangle", l: "Rect." }, { id: "oval", l: "Ovale" }].map((s) => (
                        <button
                          key={s.id}
                          onClick={() => setPoolShape(s.id)}
                          className={`flex-1 py-2.5 md:py-1.5 rounded-lg text-sm md:text-[10px] font-medium transition-all ${
                            poolShape === s.id ? "bg-[#29B6F6]/15 text-[#29B6F6] ring-1 ring-[#29B6F6]/30" : "text-[#aaa] bg-[#0d0d0d]"
                          }`}
                          data-testid={`pool-shape-${s.id}`}
                        >
                          {s.l}
                        </button>
                      ))}
                    </div>
                    <NI label="Long." value={poolL} onChange={setPoolL} min={1} max={Math.min(terrL - 0.5, 10)} color="#29B6F6" />
                    <NI label="Larg." value={poolW} onChange={setPoolW} min={1} max={Math.min(terrW - 0.5, 8)} color="#29B6F6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </section>

            <div className="border-t border-[#1f1f1f]" />

            {/* ── CTA ── */}
            {materials && (
              <Link
                to={`/demande-de-devis?surface=${materials.area}&lames=${materials.lames}&product=${product.name}&lambourdes=${materials.lambourdes}&clips=${materials.clips}&demiclips=${materials.demiClips}&forme=${shape}`}
                className="flex items-center justify-center gap-2 w-full rounded-xl px-4 py-3 text-sm font-semibold text-white transition-all active:scale-[0.97]"
                style={{ background: "linear-gradient(135deg, #8BC34A, #689F38)", boxShadow: "0 4px 20px rgba(139,195,74,0.3)" }}
                data-testid="mobile-devis-cta"
              >
                Demander un devis <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </aside>

        {/* ═══ CANVAS ════════════════════════════════ */}
        <main
          className="flex-1 order-1 md:order-2 relative flex items-center justify-center overflow-hidden"
          style={{ background: "#0a0a0a", minHeight: "320px" }}
          data-testid="mobile-preview"
        >
          {/* Info bar */}
          <div className="absolute top-2 left-2 md:top-3 md:left-4 z-20 flex flex-col md:flex-row items-start md:items-center gap-1 md:gap-3 bg-black/70 md:bg-transparent rounded-xl px-3 py-2 md:px-0 md:py-0" data-testid="info-bar">
            <span className="text-sm md:text-[11px] font-mono font-bold text-white whitespace-nowrap">
              {terrL.toFixed(1)} × {terrW.toFixed(1)}m
            </span>
            <span className="text-sm md:text-[11px] font-mono font-bold text-[#8BC34A] whitespace-nowrap">
              {netA.toFixed(1)} m² net
            </span>
          </div>

          {/* Hint (mobile only) */}
          {!isDesktop && (
            <div className="absolute top-2 right-2 z-20 text-xs text-[#aaa] bg-black/70 rounded-lg px-2 py-1">
              {dragEdge ? "Relâchez pour valider" : "Glissez les bords verts"}
            </div>
          )}

          {/* ── Terrace + handles wrapper ── */}
          <div
            className="relative"
            style={{
              width: `${tW}%`,
              maxWidth: "85%",
              ...(isDesktop
                ? { aspectRatio: `${terrL} / ${terrW}`, maxHeight: "75%" }
                : { paddingBottom: `${tH}%` }
              ),
            }}
          >
            <div ref={terrRef} className="absolute inset-0 rounded-lg overflow-hidden">
              <PlanksGrid direction={dir} product={product} numPlanks={numPlanks} clipPath={clipPath} isDesktop={isDesktop} />
              {hasPool && (
                <PoolElement
                  poolShape={poolShape} poolPos={poolPos}
                  pctW={ppW} pctH={ppH}
                  isDragging={isDragPool}
                  onStart={() => setIsDragPool(true)}
                />
              )}
            </div>

            {/* ── Drag handles (mobile only) ── */}
            {!isDesktop && (
              <>
                <div
                  className={`absolute z-20 flex items-center justify-center rounded-full touch-none transition-all duration-150
                    ${dragEdge === "right" ? "bg-[#8BC34A] shadow-lg shadow-[#8BC34A]/40 scale-110" : "bg-[#8BC34A]/40 hover:bg-[#8BC34A]/80"}`}
                  style={{ width: "14px", height: "52px", right: "-7px", top: "50%", transform: "translateY(-50%)", cursor: "ew-resize" }}
                  onTouchStart={handleEdgeDrag("right")}
                  data-testid="handle-right"
                >
                  <GripVertical className="w-2.5 h-2.5 text-white/80" />
                </div>
                <div
                  className={`absolute z-20 flex items-center justify-center rounded-full touch-none transition-all duration-150
                    ${dragEdge === "bottom" ? "bg-[#8BC34A] shadow-lg shadow-[#8BC34A]/40 scale-110" : "bg-[#8BC34A]/40 hover:bg-[#8BC34A]/80"}`}
                  style={{ height: "14px", width: "52px", bottom: "-7px", left: "50%", transform: "translateX(-50%)", cursor: "ns-resize" }}
                  onTouchStart={handleEdgeDrag("bottom")}
                  data-testid="handle-bottom"
                >
                  <GripHorizontal className="w-2.5 h-2.5 text-white/80" />
                </div>
              </>
            )}

            {/* ── Edge dimension labels ── */}
            <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 z-20 text-xs md:text-[11px] font-mono font-bold text-[#8BC34A]">
              {terrL.toFixed(1)}m
            </div>
            <div className="absolute top-1/2 -left-10 md:-left-9 -translate-y-1/2 -rotate-90 z-20 text-xs md:text-[11px] font-mono font-bold text-[#8BC34A]">
              {terrW.toFixed(1)}m
            </div>
          </div>

          {/* ── Floating Estimation Card (desktop only) ── */}
          <div className="absolute bottom-4 right-4 z-30 hidden md:block max-w-[280px]">
            <EstCard materials={materials} product={product} />
          </div>
        </main>

        {/* ═══ MOBILE ESTIMATION (between canvas and sidebar) ═══ */}
        <div className="order-2 md:hidden px-3 py-3" style={{ background: "#0d0d0d" }}>
          <EstCard materials={materials} product={product} />
        </div>
      </div>
    </div>
  );
}
